import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  Link,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ShoppingCart as ShoppingCartIcon,
  ExpandMore as ExpandMoreIcon,
  CalendarToday as CalendarTodayIcon,
  PersonOutline as PersonOutlineIcon,
  TrendingUp as TrendingUpIcon,
  Add as AddIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import Can from '../../components/Can';
import VendedorSelect from '../../components/VendedorSelect';
import { ROUTES, pedidoPosPath } from '../../config/routes';
import crmActividadService from '../../services/crmActividadService';
import crmCatalogoService from '../../services/crmCatalogoService';
import crmContactoService from '../../services/crmContactoService';
import crmOportunidadService from '../../services/crmOportunidadService';
import crmPipelineService from '../../services/crmPipelineService';
import type {
  CrmActividad,
  CrmContactoListItem,
  CrmMotivoPerdida,
  CrmOportunidad,
  CrmOrigen,
  CrmPipeline,
  CrmPipelineEtapa,
} from '../../types/crm';
import { formatCurrency } from '../../utils/formatters';
import { getApiErrorMessage } from '../../utils/apiError';
import CrmTimeline from './components/CrmTimeline';

const CrmOportunidadDetalle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const oportunidadId = Number(id);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [oportunidad, setOportunidad] = useState<CrmOportunidad | null>(null);
  const [pipelines, setPipelines] = useState<CrmPipeline[]>([]);
  const [origenes, setOrigenes] = useState<CrmOrigen[]>([]);
  const [motivos, setMotivos] = useState<CrmMotivoPerdida[]>([]);
  const [contactosOpts, setContactosOpts] = useState<CrmContactoListItem[]>([]);
  const [actividades, setActividades] = useState<CrmActividad[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [nombre, setNombre] = useState('');
  const [pipelineId, setPipelineId] = useState<number | ''>('');
  const [etapaId, setEtapaId] = useState<number | ''>('');
  const [nroVendedor, setNroVendedor] = useState<number | null>(null);
  const [montoEstimado, setMontoEstimado] = useState('');
  const [usarSuma, setUsarSuma] = useState(true);
  const [probabilidad, setProbabilidad] = useState('');
  const [fechaCierreEstimada, setFechaCierreEstimada] = useState('');
  const [origenId, setOrigenId] = useState<number | ''>('');
  const [selectedContactos, setSelectedContactos] = useState<CrmContactoListItem[]>([]);
  const [contactoPrincipalId, setContactoPrincipalId] = useState<number | ''>('');
  const [cotizacionIds, setCotizacionIds] = useState('');

  const [perdidaOpen, setPerdidaOpen] = useState(false);
  const [motivoPerdidaId, setMotivoPerdidaId] = useState<number | ''>('');
  const [showComercial, setShowComercial] = useState(false);
  const [showPipeline, setShowPipeline] = useState(false);
  const [showContactos, setShowContactos] = useState(false);
  const [showCotizaciones, setShowCotizaciones] = useState(true);
  const [showActividades, setShowActividades] = useState(true);
  const [solapaIzq, setSolapaIzq] = useState(0);
  const [mostrarVincularCotizacion, setMostrarVincularCotizacion] = useState(false);
  const [mostrarNuevaActividad, setMostrarNuevaActividad] = useState(false);

  const etapas: CrmPipelineEtapa[] = useMemo(() => {
    if (pipelineId === '') return [];
    const pipe = pipelines.find((p) => p.id === pipelineId);
    return [...(pipe?.etapas ?? [])].sort((a, b) => a.orden - b.orden);
  }, [pipelines, pipelineId]);

  const load = async () => {
    if (!oportunidadId || Number.isNaN(oportunidadId)) return;
    try {
      setLoading(true);
      const [opp, pipes, orgs, mots, contactsPage] = await Promise.all([
        crmOportunidadService.getById(oportunidadId),
        crmPipelineService.getAll(),
        crmCatalogoService.getOrigenes(),
        crmCatalogoService.getMotivosPerdida(),
        crmContactoService.getAll({ pageNumber: 1, pageSize: 100, activo: true }),
      ]);
      setOportunidad(opp);
      setPipelines(pipes ?? []);
      setOrigenes(orgs ?? []);
      setMotivos((mots ?? []).filter((m) => m.activo));
      setContactosOpts(contactsPage.items ?? []);

      setNombre(opp.nombre ?? '');
      setPipelineId(opp.pipelineId);
      setEtapaId(opp.etapaId);
      setNroVendedor(opp.nroVendedor);
      setMontoEstimado(opp.montoEstimado != null ? String(opp.montoEstimado) : '');
      setUsarSuma(opp.usarSumaCotizaciones);
      setProbabilidad(opp.probabilidad != null ? String(opp.probabilidad) : '');
      setFechaCierreEstimada(
        opp.fechaCierreEstimada ? opp.fechaCierreEstimada.substring(0, 10) : '',
      );
      setOrigenId(opp.origenId ?? '');
      setContactoPrincipalId(
        opp.contactos.find((c) => c.esPrincipal)?.contactoId ??
          opp.contactos[0]?.contactoId ??
          '',
      );
      setCotizacionIds((opp.cotizaciones ?? []).map((c) => c.nroFormulario).join(', '));

      const contactoIds = new Set((opp.contactos ?? []).map((c) => c.contactoId));
      const fromList = (contactsPage.items ?? []).filter((c) => contactoIds.has(c.id));
      const missing = (opp.contactos ?? [])
        .filter((c) => !fromList.some((x) => x.id === c.contactoId))
        .map(
          (c) =>
            ({
              id: c.contactoId,
              nombre: c.nombre,
              empresa: c.empresa,
              email: c.email,
              nroVendedor: opp.nroVendedor,
              activo: true,
              tags: [],
              fechaAlta: opp.fechaAlta,
            }) as CrmContactoListItem,
        );
      setSelectedContactos([...fromList, ...missing]);

      const acts = await crmActividadService.getAll({ oportunidadId });
      setActividades(Array.isArray(acts) ? acts : []);
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cargar la oportunidad'), {
        variant: 'error',
      });
      navigate(ROUTES.crm.pipeline);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [oportunidadId]);

  const handleSave = async () => {
    if (!nombre.trim() || pipelineId === '') {
      enqueueSnackbar('Nombre y pipeline son obligatorios', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const nrosCot = cotizacionIds
        .split(/[,;\s]+/)
        .map((x) => Number(x.trim()))
        .filter((n) => !Number.isNaN(n) && n > 0);

      const updated = await crmOportunidadService.update(oportunidadId, {
        nombre: nombre.trim(),
        pipelineId,
        etapaId: etapaId === '' ? null : etapaId,
        nroVendedor,
        montoEstimado: montoEstimado ? Number(montoEstimado) : null,
        usarSumaCotizaciones: usarSuma,
        probabilidad: probabilidad ? Number(probabilidad) : null,
        fechaCierreEstimada: fechaCierreEstimada || null,
        origenId: origenId === '' ? null : origenId,
        contactoIds: selectedContactos.map((c) => c.id),
        contactoPrincipalId: contactoPrincipalId === '' ? null : contactoPrincipalId,
        nroFormulariosCotizacion: nrosCot.length ? nrosCot : null,
      });
      setOportunidad(updated);
      setPipelineId(updated.pipelineId);
      setEtapaId(updated.etapaId);
      setProbabilidad(updated.probabilidad != null ? String(updated.probabilidad) : '');
      enqueueSnackbar('Oportunidad actualizada', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al guardar'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCerrarGanada = async () => {
    try {
      const updated = await crmOportunidadService.cerrarGanada(oportunidadId, {});
      setOportunidad(updated);
      enqueueSnackbar('Oportunidad marcada como ganada', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cerrar como ganada'), { variant: 'error' });
    }
  };

  const handleCerrarPerdida = async () => {
    if (motivoPerdidaId === '') {
      enqueueSnackbar('Seleccioná un motivo de pérdida', { variant: 'warning' });
      return;
    }
    try {
      const updated = await crmOportunidadService.cerrarPerdida(oportunidadId, {
        motivoPerdidaId,
      });
      setOportunidad(updated);
      setPerdidaOpen(false);
      enqueueSnackbar('Oportunidad marcada como perdida', { variant: 'success' });
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, 'Error al cerrar como perdida'), { variant: 'error' });
    }
  };

  const cotizacionPrincipal = oportunidad?.cotizaciones?.[0];
  const esGanada = (oportunidad?.estado || '').toLowerCase().includes('ganad');

  if (loading || !oportunidad) {
    return (
      <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
        <Typography>Cargando...</Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        bgcolor: '#f5f5f5',
        minHeight: '100vh',
        maxWidth: '100%',
        overflowX: 'hidden',
      }}
    >
      <Card
        sx={{
          bgcolor: '#ffffff',
          borderRadius: 3,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: 3, flexWrap: 'wrap', gap: 1 }}
          >
            <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
              <Button
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate(ROUTES.crm.pipeline)}
                sx={{ textTransform: 'none' }}
              >
                Pipeline
              </Button>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {oportunidad.nombre}
              </Typography>
              <Chip
                size="small"
                label={oportunidad.estado}
                color={
                  esGanada ? 'success' : oportunidad.estado === 'Perdida' ? 'error' : 'default'
                }
                sx={{ height: 22, fontSize: '0.75rem' }}
              />
              <Chip
                size="small"
                label={formatCurrency(oportunidad.monto)}
                color="primary"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.75rem', fontWeight: 600 }}
              />
            </Stack>
          </Stack>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={8}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs
                  value={solapaIzq}
                  onChange={(_, v) => setSolapaIzq(v)}
                  textColor="primary"
                  indicatorColor="primary"
                >
                  <Tab label="Oportunidad" sx={{ textTransform: 'none', minHeight: 42 }} />
                  <Tab
                    label={`Historial de etapas${
                      (oportunidad.historialEtapas?.length ?? 0) > 0
                        ? ` (${oportunidad.historialEtapas.length})`
                        : ''
                    }`}
                    sx={{ textTransform: 'none', minHeight: 42 }}
                  />
                </Tabs>
              </Box>

              {solapaIzq === 0 && (
                <Stack spacing={2.5}>
                  <Box>
                    <Card variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent
                        sx={{ py: 1.5, px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: 1.5 } }}
                      >
                        <Stack spacing={1.5}>
                          <TextField
                            label="Nombre"
                            required
                            fullWidth
                            size="small"
                            value={nombre}
                            onChange={(e) => setNombre(e.target.value)}
                          />
                          <Stack
                            direction="row"
                            spacing={2}
                            flexWrap="wrap"
                            useFlexGap
                            alignItems="center"
                            sx={{ px: 0.25 }}
                          >
                            {(() => {
                              const pct =
                                probabilidad !== '' ? Number(probabilidad) : null;
                              const chipSx =
                                pct == null || Number.isNaN(pct)
                                  ? {
                                      bgcolor: 'grey.200',
                                      color: 'text.secondary',
                                    }
                                  : pct >= 70
                                    ? { bgcolor: '#2e7d32', color: '#fff' }
                                    : pct >= 40
                                      ? { bgcolor: '#ed6c02', color: '#fff' }
                                      : { bgcolor: '#d32f2f', color: '#fff' };
                              return (
                                <Chip
                                  size="small"
                                  label={
                                    pct != null && !Number.isNaN(pct)
                                      ? `Probabilidad ${pct}%`
                                      : 'Probabilidad —'
                                  }
                                  sx={{
                                    height: 22,
                                    fontSize: '0.75rem',
                                    fontWeight: 600,
                                    ...chipSx,
                                  }}
                                />
                              );
                            })()}
                            <Typography variant="body2" color="text.secondary">
                              Cierre estimado:{' '}
                              <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                {fechaCierreEstimada
                                  ? new Date(`${fechaCierreEstimada}T00:00:00`).toLocaleDateString(
                                      'es-AR',
                                    )
                                  : '—'}
                              </Box>
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Origen:{' '}
                              <Box component="span" sx={{ color: 'text.primary', fontWeight: 600 }}>
                                {origenId !== ''
                                  ? origenes.find((o) => o.id === origenId)?.nombre || '—'
                                  : '—'}
                              </Box>
                            </Typography>
                          </Stack>
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {oportunidad.etapaNombre && (
                              <Chip
                                size="small"
                                label={oportunidad.etapaNombre}
                                sx={{
                                  height: 20,
                                  fontSize: '0.7rem',
                                  bgcolor: oportunidad.etapaColor || undefined,
                                  color: oportunidad.etapaColor ? '#fff' : undefined,
                                }}
                              />
                            )}
                            {oportunidad.nombreVendedor && (
                              <Typography variant="caption" color="text.secondary">
                                Vendedor: {oportunidad.nombreVendedor}
                              </Typography>
                            )}
                            {oportunidad.contactoPrincipal && (
                              <Typography variant="caption" color="text.secondary">
                                Contacto: {oportunidad.contactoPrincipal}
                              </Typography>
                            )}
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Box>

                  <Accordion
                    expanded={showCotizaciones}
                    onChange={() => setShowCotizaciones((p) => !p)}
                    variant="outlined"
                    disableGutters
                    sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ width: '100%', pr: 1 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Cotizaciones vinculadas
                          {(oportunidad.cotizaciones?.length ?? 0) > 0
                            ? ` (${oportunidad.cotizaciones.length})`
                            : ''}
                        </Typography>
                        {!mostrarVincularCotizacion && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCotizaciones(true);
                              setMostrarVincularCotizacion(true);
                            }}
                            sx={{ textTransform: 'none' }}
                          >
                            Vincular
                          </Button>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={1.5}>
                        {mostrarVincularCotizacion && (
                          <Stack
                            spacing={1.5}
                            sx={{ p: 1.5, bgcolor: '#fafafa', borderRadius: 2 }}
                          >
                            <TextField
                              label="Nros. cotización"
                              size="small"
                              fullWidth
                              value={cotizacionIds}
                              onChange={(e) => setCotizacionIds(e.target.value)}
                              helperText="Separados por coma. Se vinculan al guardar."
                              placeholder="Ej: 1001, 1002"
                              autoFocus
                            />
                            <Stack direction="row" spacing={1}>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => setMostrarVincularCotizacion(false)}
                                disabled={!cotizacionIds.trim()}
                                sx={{ textTransform: 'none' }}
                              >
                                Listo
                              </Button>
                              <Button
                                size="small"
                                startIcon={<CloseIcon />}
                                onClick={() => {
                                  setCotizacionIds(
                                    (oportunidad.cotizaciones ?? [])
                                      .map((c) => String(c.nroFormulario))
                                      .join(', '),
                                  );
                                  setMostrarVincularCotizacion(false);
                                }}
                                sx={{ textTransform: 'none' }}
                              >
                                Cancelar
                              </Button>
                            </Stack>
                          </Stack>
                        )}
                        {(oportunidad.cotizaciones?.length ?? 0) === 0 &&
                        !cotizacionIds.trim() ? (
                          <Typography variant="body2" color="text.secondary">
                            Sin cotizaciones asociadas todavía.
                          </Typography>
                        ) : (
                          <>
                            {(oportunidad.cotizaciones?.length ?? 0) > 0 && (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Nro.</TableCell>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell align="right">Importe</TableCell>
                                    <TableCell>Estado</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {oportunidad.cotizaciones.map((c) => (
                                    <TableRow key={c.nroFormulario} hover>
                                      <TableCell>
                                        <Link
                                          component={RouterLink}
                                          to={ROUTES.ventas.cotizaciones.ver(c.nroFormulario)}
                                        >
                                          {c.nroFormulario}
                                        </Link>
                                      </TableCell>
                                      <TableCell>{c.nombre || '-'}</TableCell>
                                      <TableCell align="right">
                                        {c.importeTotal != null
                                          ? formatCurrency(c.importeTotal)
                                          : '-'}
                                      </TableCell>
                                      <TableCell>{c.estado || '-'}</TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            )}
                            {cotizacionIds.trim() && !mostrarVincularCotizacion && (
                              <Typography variant="caption" color="text.secondary">
                                Pendiente de vincular al guardar: {cotizacionIds}
                              </Typography>
                            )}
                          </>
                        )}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={showActividades}
                    onChange={() => setShowActividades((p) => !p)}
                    variant="outlined"
                    disableGutters
                    sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}
                    >
                      <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        spacing={1}
                        sx={{ width: '100%', pr: 1 }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Actividades
                          {actividades.length > 0 ? ` (${actividades.length})` : ''}
                        </Typography>
                        {!mostrarNuevaActividad && (
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<AddIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowActividades(true);
                              setMostrarNuevaActividad(true);
                            }}
                            sx={{ textTransform: 'none' }}
                          >
                            Nueva actividad
                          </Button>
                        )}
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <CrmTimeline
                        hideHeader
                        formOpen={mostrarNuevaActividad}
                        onFormOpenChange={setMostrarNuevaActividad}
                        actividades={actividades}
                        onCreate={async (dto) => {
                          try {
                            await crmActividadService.create({ ...dto, oportunidadId });
                            enqueueSnackbar('Actividad agregada', { variant: 'success' });
                            const acts = await crmActividadService.getAll({ oportunidadId });
                            setActividades(Array.isArray(acts) ? acts : []);
                          } catch (error) {
                            enqueueSnackbar(
                              getApiErrorMessage(error, 'Error al crear actividad'),
                              { variant: 'error' },
                            );
                            throw error;
                          }
                        }}
                      />
                    </AccordionDetails>
                  </Accordion>
                </Stack>
              )}

              {solapaIzq === 1 && (
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent sx={{ py: 1.5, px: { xs: 1.5, sm: 2 }, '&:last-child': { pb: 1.5 } }}>
                    {(oportunidad.historialEtapas?.length ?? 0) === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        Sin movimientos de etapa.
                      </Typography>
                    ) : (
                      <Stack spacing={1}>
                        {oportunidad.historialEtapas.map((h) => (
                          <Box
                            key={h.id}
                            sx={{
                              py: 1,
                              px: 1.25,
                              borderRadius: 1.5,
                              bgcolor: '#fafafa',
                              border: '1px solid',
                              borderColor: 'divider',
                            }}
                          >
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {h.etapaNuevaNombre || h.etapaNuevaId}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {new Date(h.fecha).toLocaleString('es-AR')} · {h.usuario}
                              {h.diasEnEtapaAnterior != null
                                ? ` · ${h.diasEnEtapaAnterior}d en etapa anterior`
                                : ''}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              )}
            </Grid>

            <Grid item xs={12} md={4}>
              <Box sx={{ position: { md: 'sticky' }, top: { md: 16 } }}>
                <Stack spacing={1.5}>
                  <Box
                    sx={{
                      p: 1.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'info.light',
                      bgcolor: 'rgba(33,150,243,0.04)',
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" display="block">
                      Monto actual
                    </Typography>
                    <Typography variant="h5" color="primary.main" sx={{ fontWeight: 700 }}>
                      {formatCurrency(oportunidad.monto)}
                    </Typography>
                    {oportunidad.probabilidad != null && (
                      <Typography variant="caption" color="text.secondary">
                        Probabilidad {oportunidad.probabilidad}% · Forecast{' '}
                        {formatCurrency(
                          oportunidad.monto * ((oportunidad.probabilidad ?? 0) / 100),
                        )}
                      </Typography>
                    )}
                  </Box>

                  <Accordion
                    expanded={showComercial}
                    onChange={() => setShowComercial((p) => !p)}
                    variant="outlined"
                    disableGutters
                    sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <TrendingUpIcon fontSize="small" color="action" />
                        <Typography variant="body2">Datos comerciales</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={1.5}>
                        <TextField
                          label="Monto estimado"
                          type="number"
                          size="small"
                          fullWidth
                          value={montoEstimado}
                          onChange={(e) => setMontoEstimado(e.target.value)}
                          disabled={usarSuma}
                        />
                        <FormControlLabel
                          control={
                            <Checkbox
                              size="small"
                              checked={usarSuma}
                              onChange={(e) => setUsarSuma(e.target.checked)}
                            />
                          }
                          label={
                            <Typography variant="body2">Usar suma de cotizaciones</Typography>
                          }
                        />
                        <TextField
                          label="Probabilidad %"
                          type="number"
                          size="small"
                          fullWidth
                          value={probabilidad}
                          onChange={(e) => setProbabilidad(e.target.value)}
                          inputProps={{ min: 0, max: 100 }}
                        />
                        <TextField
                          label="Cierre estimado"
                          type="date"
                          size="small"
                          fullWidth
                          InputLabelProps={{ shrink: true }}
                          value={fechaCierreEstimada}
                          onChange={(e) => setFechaCierreEstimada(e.target.value)}
                        />
                        <TextField
                          select
                          label="Origen"
                          size="small"
                          fullWidth
                          value={origenId}
                          onChange={(e) =>
                            setOrigenId(e.target.value === '' ? '' : Number(e.target.value))
                          }
                        >
                          <MenuItem value="">Sin origen</MenuItem>
                          {origenes.map((o) => (
                            <MenuItem key={o.id} value={o.id}>
                              {o.nombre}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={showPipeline}
                    onChange={() => setShowPipeline((p) => !p)}
                    variant="outlined"
                    disableGutters
                    sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <CalendarTodayIcon fontSize="small" color="action" />
                        <Typography variant="body2">Pipeline y etapa</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={1.5}>
                        <TextField
                          select
                          label="Pipeline"
                          size="small"
                          fullWidth
                          value={pipelineId}
                          onChange={(e) => {
                            const pid = Number(e.target.value);
                            setPipelineId(pid);
                            const first = pipelines.find((p) => p.id === pid)?.etapas?.[0];
                            setEtapaId(first?.id ?? '');
                          }}
                        >
                          {pipelines.map((p) => (
                            <MenuItem key={p.id} value={p.id}>
                              {p.nombre}
                            </MenuItem>
                          ))}
                        </TextField>
                        <TextField
                          select
                          label="Etapa"
                          size="small"
                          fullWidth
                          value={etapaId}
                          onChange={(e) => setEtapaId(Number(e.target.value))}
                        >
                          {etapas.map((e) => (
                            <MenuItem key={e.id} value={e.id}>
                              {e.nombre}
                            </MenuItem>
                          ))}
                        </TextField>
                        <VendedorSelect
                          value={nroVendedor}
                          onChange={setNroVendedor}
                          size="small"
                          nombreVendedorFallback={oportunidad.nombreVendedor}
                        />
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  <Accordion
                    expanded={showContactos}
                    onChange={() => setShowContactos((p) => !p)}
                    variant="outlined"
                    disableGutters
                    sx={{ borderRadius: '8px !important', '&:before': { display: 'none' } }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      sx={{ minHeight: 44, '& .MuiAccordionSummary-content': { my: 0 } }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PersonOutlineIcon fontSize="small" color="action" />
                        <Typography variant="body2">Contactos</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails sx={{ pt: 0 }}>
                      <Stack spacing={1.5}>
                        <Autocomplete
                          multiple
                          size="small"
                          options={contactosOpts}
                          value={selectedContactos}
                          getOptionLabel={(o) =>
                            `${o.nombre}${o.empresa ? ` (${o.empresa})` : ''}`
                          }
                          isOptionEqualToValue={(a, b) => a.id === b.id}
                          onChange={(_, value) => {
                            setSelectedContactos(value);
                            if (value.length && !value.some((c) => c.id === contactoPrincipalId)) {
                              setContactoPrincipalId(value[0].id);
                            }
                          }}
                          renderInput={(params) => (
                            <TextField {...params} label="Contactos" size="small" />
                          )}
                        />
                        <TextField
                          select
                          label="Contacto principal"
                          size="small"
                          fullWidth
                          value={contactoPrincipalId}
                          onChange={(e) =>
                            setContactoPrincipalId(
                              e.target.value === '' ? '' : Number(e.target.value),
                            )
                          }
                        >
                          <MenuItem value="">Sin principal</MenuItem>
                          {selectedContactos.map((c) => (
                            <MenuItem key={c.id} value={c.id}>
                              {c.nombre}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>

                  <Stack spacing={1}>
                    {esGanada && cotizacionPrincipal && (
                      <Can permiso="crm.oportunidades.cerrar">
                        <Button
                          variant="contained"
                          color="success"
                          fullWidth
                          startIcon={<ShoppingCartIcon />}
                          onClick={() =>
                            navigate(
                              pedidoPosPath({ cotizacionId: cotizacionPrincipal.nroFormulario }),
                            )
                          }
                          sx={{ textTransform: 'none' }}
                        >
                          Preparar pedido
                        </Button>
                      </Can>
                    )}
                    <Can permiso="crm.oportunidades.cerrar">
                      <Button
                        variant="outlined"
                        color="success"
                        fullWidth
                        startIcon={<CheckCircleIcon />}
                        onClick={handleCerrarGanada}
                        disabled={oportunidad.estado !== 'Abierta'}
                        sx={{ textTransform: 'none' }}
                      >
                        Cerrar ganada
                      </Button>
                    </Can>
                    <Can permiso="crm.oportunidades.cerrar">
                      <Button
                        variant="outlined"
                        color="error"
                        fullWidth
                        startIcon={<CancelIcon />}
                        onClick={() => setPerdidaOpen(true)}
                        disabled={oportunidad.estado !== 'Abierta'}
                        sx={{ textTransform: 'none' }}
                      >
                        Cerrar perdida
                      </Button>
                    </Can>
                    <Can permiso="crm.oportunidades.editar">
                      <Button
                        variant="contained"
                        color="primary"
                        fullWidth
                        startIcon={<SaveIcon />}
                        onClick={handleSave}
                        disabled={saving}
                        sx={{ py: 1.2, fontWeight: 600, textTransform: 'none' }}
                      >
                        Guardar
                      </Button>
                    </Can>
                    <Button
                      variant="outlined"
                      fullWidth
                      onClick={() => navigate(ROUTES.crm.pipeline)}
                      disabled={saving}
                      sx={{ textTransform: 'none' }}
                    >
                      Cerrar
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Dialog open={perdidaOpen} onClose={() => setPerdidaOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Cerrar como perdida</DialogTitle>
        <DialogContent>
          <TextField
            select
            fullWidth
            size="small"
            label="Motivo"
            value={motivoPerdidaId}
            onChange={(e) => setMotivoPerdidaId(Number(e.target.value))}
            sx={{ mt: 1 }}
          >
            {motivos.map((m) => (
              <MenuItem key={m.id} value={m.id}>
                {m.nombre}
              </MenuItem>
            ))}
          </TextField>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPerdidaOpen(false)}>Cancelar</Button>
          <Button variant="contained" color="error" onClick={handleCerrarPerdida}>
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrmOportunidadDetalle;
